import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ))}
  </div>
);

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fallbackReviews = [
    { id: '1', author_name: 'sassychic', company: '', role: 'Customer', rating: 5, text: 'I recently had a delivery transported from South Australia to Darwin, and I couldn\'t be happier with the entire experience. The team was professional, communicative and ensured my delivery arrived safely and on time. Highly recommend!' },
    { id: '2', author_name: 'Kimberley Priest', company: '', role: 'Customer', rating: 5, text: 'Fantastic platform for connecting shippers with carriers across Australia. Great service and easy to use. Highly recommend Truckn Send It!' }
  ];

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/api/reviews`);
        if (response.data && response.data.length > 0) {
          setReviews(response.data);
        } else {
          setReviews(fallbackReviews);
        }
      } catch {
        setReviews(fallbackReviews);
      }
    };
    fetchReviews();
  }, []);

  if (reviews.length === 0) return null;

  const next = () => setCurrentIndex((prev) => (prev + 1) % reviews.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  const current = reviews[currentIndex];

  const avgRating = (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1);

  return (
    <div className="py-12 sm:py-16 md:py-20 bg-white" data-testid="reviews-section">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header with Google-style rating summary */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
              <span className="text-sm font-medium text-gray-500">Google Reviews</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <span className="text-3xl font-bold text-gray-800">{avgRating}</span>
              <div>
                <StarRating rating={Math.round(parseFloat(avgRating))} />
                <p className="text-xs text-gray-500 mt-0.5">{reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}</p>
              </div>
            </div>
          </div>

          {/* Review Card */}
          <div className="bg-gray-50 p-6 sm:p-8 md:p-10 rounded-xl shadow-sm relative border border-gray-100">
            <Quote className="absolute top-6 left-6 w-10 h-10 text-red-600 opacity-20" />
            
            <div className="relative z-10">
              <StarRating rating={current.rating} />
              
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mt-4 mb-6 italic" data-testid="review-text">
                "{current.text}"
              </p>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="font-bold text-[#1e3a5f] text-base" data-testid="review-author">{current.author_name}</p>
                  <p className="text-gray-500 text-sm">
                    {[current.role, current.company].filter(Boolean).join(' — ')}
                  </p>
                </div>
                
                {reviews.length > 1 && (
                  <div className="flex gap-2">
                    <Button onClick={prev} variant="outline" size="sm" className="border-gray-300 h-8 w-8 p-0">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button onClick={next} variant="outline" size="sm" className="border-gray-300 h-8 w-8 p-0">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Dot indicators */}
          {reviews.length > 1 && (
            <div className="flex justify-center mt-5 gap-2">
              {reviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-red-600 w-6' : 'bg-gray-300 w-2'
                  }`}
                  data-testid={`review-dot-${index}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
